/**
 * AriaThinkingTrail — the green progress bars from the Build chat,
 * generalized for any Aria-powered chat surface.
 *
 * Renders a vertical list of context-lookup steps Aria performed before
 * answering. Each step shows a label, a one-liner detail, a small badge
 * ("PLAN ✓", "🔴 DRIFT"…) and pulses while the agent is still composing.
 *
 * The trail is returned by `POST /api/aria/chat` in the `trail` field.
 * Pass `pulsing={true}` while the fetch is in flight to animate it,
 * `pulsing={false}` once the reply lands.
 */
import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';

export default function AriaThinkingTrail({ trail = [], pulsing = false, compact = false }) {
  if (!trail || trail.length === 0) return null;
  return (
    <div data-testid="aria-thinking-trail" style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: compact ? '6px 10px' : '10px 14px',
      background: '#F4F8F1',
      border: '1px solid #C8E6BD',
      borderRadius: 10,
      fontSize: compact ? 10 : 11,
      fontFamily: '"SF Mono", "JetBrains Mono", Menlo, monospace',
    }}>
      {trail.map((t, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          opacity: pulsing && i === trail.length - 1 ? 0.6 : 1,
          animation: pulsing && i === trail.length - 1
            ? 'trailPulse 1.2s ease-in-out infinite' : 'none',
        }}>
          {t.ok
            ? <Check size={11} color="#2E7D32" strokeWidth={3} />
            : <AlertTriangle size={11} color="#EF4444" strokeWidth={2.5} />}
          <span style={{ fontWeight: 700, color: t.ok ? '#1B5E20' : '#7F1D1D', minWidth: 110 }}>
            {t.label}
          </span>
          <span style={{ color: '#3F583B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.detail}
          </span>
          {t.badge && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.14em',
              color: t.ok ? '#2E7D32' : '#B91C1C',
              background: t.ok ? '#C8E6BD' : '#FECACA',
              padding: '1px 6px', borderRadius: 4,
            }}>{t.badge}</span>
          )}
        </div>
      ))}
      <style>{`
        @keyframes trailPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}
